import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { accuracy, countChars, netWpm } from "../lib/typing";
import { generatePassage } from "../lib/words";

export type TypingStatus = "idle" | "running" | "done";

export interface TypingApi {
  text: string;
  typed: string;
  status: TypingStatus;
  /** Seconds elapsed (frozen when done). */
  elapsed: number;
  /** Seconds left in the test. */
  remaining: number;
  wpm: number;
  accuracy: number;
  correct: number;
  incorrect: number;
  durationSec: number;
  onType: (value: string) => void;
  restart: () => void;
}

/**
 * Deterministic PRNG (mulberry32). The first passage is generated with a fixed
 * seed so server and client first render produce identical text — no hydration
 * mismatch and no layout shift (the passage is present on first paint). Restart
 * uses Math.random for a fresh, varied passage after the user interacts.
 */
function seededRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Typing-test engine. Timing is timestamp-based for accuracy.
 */
export function useTypingTest(durationSec: number): TypingApi {
  // ~2.5 words/sec upper bound keeps even fast typists from running out of text.
  const wordCount = Math.max(40, Math.ceil(durationSec * 2.5));

  const [text, setText] = useState(() =>
    generatePassage(wordCount, seededRng(wordCount)),
  );
  const [typed, setTyped] = useState("");
  const [status, setStatus] = useState<TypingStatus>("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const startRef = useRef<number | null>(null);
  const statusRef = useRef<TypingStatus>("idle");
  statusRef.current = status;

  useEffect(() => {
    if (status !== "running") return;
    const id = setInterval(() => {
      if (startRef.current == null) return;
      const ms = Date.now() - startRef.current;
      if (ms >= durationSec * 1000) {
        setElapsedMs(durationSec * 1000);
        setStatus("done");
        startRef.current = null;
      } else {
        setElapsedMs(ms);
      }
    }, 100);
    return () => clearInterval(id);
  }, [status, durationSec]);

  const onType = useCallback(
    (value: string) => {
      if (statusRef.current === "done" || !text) return;
      const capped = value.slice(0, text.length);

      if (statusRef.current === "idle" && capped.length > 0) {
        startRef.current = Date.now();
        setStatus("running");
      }
      setTyped(capped);

      // Finished the whole passage before time ran out.
      if (capped.length >= text.length && startRef.current != null) {
        setElapsedMs(Date.now() - startRef.current);
        setStatus("done");
        startRef.current = null;
      }
    },
    [text],
  );

  const restart = useCallback(() => {
    startRef.current = null;
    setStatus("idle");
    setTyped("");
    setElapsedMs(0);
    setText(generatePassage(wordCount));
  }, [wordCount]);

  const { correct, incorrect } = useMemo(
    () => countChars(text, typed),
    [text, typed],
  );
  const elapsed = elapsedMs / 1000;

  return {
    text,
    typed,
    status,
    elapsed,
    remaining: Math.max(0, durationSec - elapsed),
    wpm: netWpm(correct, elapsed),
    accuracy: accuracy(correct, typed.length),
    correct,
    incorrect,
    durationSec,
    onType,
    restart,
  };
}
